import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

import { useClickOutside } from "../../hooks/useClickOutside"

export type EditItemId = string | number

type EditRegistration = Readonly<{
    hasChanges: () => boolean
    cancel: () => void
    getRootEl: () => HTMLElement | null
}>

type SingleActiveEdit = Readonly<{
    register: (id: EditItemId, registration: EditRegistration) => void
    unregister: (id: EditItemId) => void
    requestEdit: (id: EditItemId, startEdit: () => void) => void
}>

const SingleActiveEditContext = createContext<SingleActiveEdit | null>(null)

export function useSingleActiveEdit(): SingleActiveEdit {
    const context = useContext(SingleActiveEditContext)
    if (!context) {
        throw new Error("useSingleActiveEdit must be used within a SingleActiveEditProvider")
    }
    return context
}

// Registers an editable item with the provider; returns a `rootRef` to mark its bounds and a `requestEdit`
// that enforces the single-active-editor + unsaved-changes guard.
export function useSingleActiveEditItem<TElement extends HTMLElement = HTMLElement>(
    id: EditItemId,
    edit: Readonly<{ hasChanges: boolean; cancel: () => void }>,
) {
    const coordinator = useSingleActiveEdit()
    const rootRef = useRef<TElement | null>(null)
    const editRef = useRef(edit)
    editRef.current = edit

    useEffect(() => {
        coordinator.register(id, {
            hasChanges: () => editRef.current.hasChanges,
            cancel: () => editRef.current.cancel(),
            getRootEl: () => rootRef.current,
        })
        return () => coordinator.unregister(id)
    }, [coordinator, id])

    const requestEdit = useCallback(
        (startEdit: () => void) => coordinator.requestEdit(id, startEdit),
        [coordinator, id],
    )

    return { rootRef, requestEdit }
}

type DiscardConfirmProps = Readonly<{
    open: boolean
    onKeepEditing: () => void
    onDiscard: () => void
}>

type Props = Readonly<{
    children: ReactNode
    renderDiscardConfirm: (props: DiscardConfirmProps) => ReactNode
}>

export function SingleActiveEditProvider({ children, renderDiscardConfirm }: Props) {
    const registrationsRef = useRef(new Map<EditItemId, EditRegistration>())
    const activeIdRef = useRef<EditItemId | null>(null)
    const pendingActionRef = useRef<(() => void) | null>(null)
    const [hasActive, setHasActive] = useState(false)
    const [isConfirmOpen, setConfirmOpen] = useState(false)

    const setActive = useCallback((id: EditItemId | null) => {
        activeIdRef.current = id
        setHasActive(id !== null)
    }, [])

    const register = useCallback((id: EditItemId, registration: EditRegistration) => {
        registrationsRef.current.set(id, registration)
    }, [])

    const unregister = useCallback(
        (id: EditItemId) => {
            registrationsRef.current.delete(id)
            if (activeIdRef.current === id) setActive(null)
        },
        [setActive],
    )

    const getActiveRegistration = useCallback((): EditRegistration | undefined => {
        const activeId = activeIdRef.current
        return activeId !== null ? registrationsRef.current.get(activeId) : undefined
    }, [])

    // Leave the active item, then run `proceed`. A clean edit is dropped at once; a dirty one opens the
    // confirmation and defers `proceed` until the user discards (see `confirmDiscard`).
    const leaveActiveThen = useCallback(
        (proceed: () => void) => {
            const active = getActiveRegistration()
            if (!active || !active.hasChanges()) {
                active?.cancel()
                proceed()
                return
            }
            pendingActionRef.current = proceed
            setConfirmOpen(true)
        },
        [getActiveRegistration],
    )

    const requestEdit = useCallback(
        (id: EditItemId, startEdit: () => void) => {
            if (activeIdRef.current === id) {
                startEdit()
                return
            }
            leaveActiveThen(() => {
                setActive(id)
                startEdit()
            })
        },
        [leaveActiveThen, setActive],
    )

    const keepEditing = useCallback(() => {
        pendingActionRef.current = null
        setConfirmOpen(false)
    }, [])

    const confirmDiscard = useCallback(() => {
        getActiveRegistration()?.cancel()
        const proceed = pendingActionRef.current
        pendingActionRef.current = null
        setConfirmOpen(false)
        proceed?.()
    }, [getActiveRegistration])

    useClickOutside(
        () => getActiveRegistration()?.getRootEl() ?? null,
        () => leaveActiveThen(() => setActive(null)),
        {
            enabled: hasActive,
        },
    )

    const value = useMemo<SingleActiveEdit>(
        () => ({ register, unregister, requestEdit }),
        [register, unregister, requestEdit],
    )

    return (
        <SingleActiveEditContext.Provider value={value}>
            {children}
            {renderDiscardConfirm({ open: isConfirmOpen, onKeepEditing: keepEditing, onDiscard: confirmDiscard })}
        </SingleActiveEditContext.Provider>
    )
}
